import React from "react";
import { saveAs } from "file-saver";
import { pdf } from "@react-pdf/renderer";
import { ReportDocument } from "../dialogs/reportPdfDialog";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { SupClass } from "../api/models/supClass.models";
import { Price } from "../api/models/prices.models";
import { Report } from "../api/models/report.models";
import { Company, Organization } from "../api/models/companies.models";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const StyledLink = styled.a`
  color: var(--sumi-blue) !important;
  text-decoration: underline !important;
  cursor: pointer;
`;

interface DownloadPdfButtonProps {
  ecomodClasses: EcomodClass[];
  supClasses: SupClass[];
  pricesData: Price[];
  prevYearReport?: Report | null;
  reportData: Report;
  reportCompany?: Company | null;
  reportOrganization?: Organization | null;
  consumer: any[];
  business: any[];
  sup: any[];
  consumerPrevYear: any[];
  businessPrevYear: any[];
  supPrevYear: any[];
  totalCost?: string;
}

const DownloadPdfButton = React.memo(
  ({
    ecomodClasses,
    supClasses,
    pricesData,
    prevYearReport,
    reportData,
    reportCompany,
    reportOrganization,
    consumer,
    business,
    sup,
    consumerPrevYear,
    businessPrevYear,
    supPrevYear,
    totalCost,
  }: DownloadPdfButtonProps) => {
    const { t } = useTranslation();

    const downloadPdf = async () => {
      // Filter data only when button is clicked, not during render
      const consumerData = consumer.filter((row) => row.combinedWeight > 0);
      const businessData = business.filter((row) => row.combinedWeight > 0);
      const supData = sup.filter((row) => row.combinedWeight > 0);
      const consumerPrevYearData = consumerPrevYear.filter(
        (row) => row.combinedWeight > 0,
      );
      const businessPrevYearData = businessPrevYear.filter(
        (row) => row.combinedWeight > 0,
      );
      const supPrevYearData = supPrevYear.filter(
        (row) => row.combinedWeight > 0,
      );

      // Generate meaningful filename
      const companyOrOrgName = reportOrganization
        ? reportOrganization.organizationName
        : reportCompany?.companyName || "Report";

      // Sanitize filename to remove invalid characters
      const sanitizedName = companyOrOrgName
        .replace(/[^a-z0-9]/gi, "_")
        .replace(/_+/g, "_");

      const year = reportData?.year || "";
      const quarter = reportData?.quarter ? `_Q${reportData.quarter}` : "";
      const month = reportData?.month ? `_M${reportData.month}` : "";
      const fileName = `${sanitizedName}_${year}${quarter}${month}.pdf`;

      const blob = await pdf(
        <ReportDocument
          reportData={reportData}
          ecomodClasses={ecomodClasses}
          supClasses={supClasses}
          pricesData={pricesData}
          prevYearReport={prevYearReport}
          reportCompany={reportCompany}
          reportOrganization={reportOrganization}
          consumerData={consumerData}
          businessData={businessData}
          supData={supData}
          prevYearConsumerData={consumerPrevYearData}
          prevYearBusinessData={businessPrevYearData}
          prevYearSupData={supPrevYearData}
          totalCost={totalCost}
        />,
      ).toBlob();
      saveAs(blob, fileName);
    };

    return (
      <StyledLink role="button" tabIndex={0} onClick={downloadPdf}>
        {t("cmd-download-report")}
      </StyledLink>
    );
  },
);

DownloadPdfButton.displayName = "DownloadPdfButton";

export default DownloadPdfButton;
